import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Dumbbell, ChevronRight, ChevronLeft, Check, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useActivityCreation } from '@/hooks/useActivityCreation';
import { format, addDays, isBefore, isAfter, isSameDay } from 'date-fns';

interface Session {
  title: string;
  description?: string;
  price_cents: number;
  duration_min: number;
  price_mode?: 'fixed' | 'per_person';
  group_min?: number;
  group_max?: number;
}

interface Trainer {
  id: string;
  name: string;
  city: string;
  image: string;
}

interface TrainerBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  trainer: Trainer;
  preSelectedSession?: Session | null;
  sessions: Session[];
}

const TrainerBookingModal: React.FC<TrainerBookingModalProps> = ({
  isOpen,
  onClose,
  trainer,
  preSelectedSession,
  sessions
}) => {
  const { isDarkMode } = useTheme();
  const { isSubmitting, isSuccess, handleCreateActivity } = useActivityCreation();
  
  // Step state
  const [step, setStep] = useState(preSelectedSession ? 2 : 1);
  
  // Form state
  const [selectedSession, setSelectedSession] = useState<Session | null>(preSelectedSession || null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Available time slots (would be fetched from API in a real implementation)
  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', 
    '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'
  ];
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(preSelectedSession ? 2 : 1);
      setSelectedSession(preSelectedSession || null);
      setSelectedDate('');
      setSelectedTime('');
      setNotes('');
      setCurrentMonth(new Date());
    }
  }, [isOpen, preSelectedSession]);
  
  // Format price for display
  const formatPrice = (cents: number, mode?: string) => {
    const price = (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    return mode === 'per_person' ? `${price}/person` : price;
  };
  
  // Generate calendar days
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add all days in the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };
  
  // Check if a date is in the past
  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return isBefore(date, today);
  };
  
  // Check if a date is selectable
  const isDateSelectable = (date: Date) => !isPastDate(date);
  
  // Format date for display
  const formatDateForDisplay = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'EEEE, MMMM d, yyyy');
  };
  
  // Check if a date is the selected date
  const isSelectedDate = (date: Date) => {
    if (!selectedDate) return false;
    return isSameDay(date, new Date(selectedDate));
  };
  
  // Handle month navigation
  const handlePreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  
  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };
  
  // Handle session selection
  const handleSessionSelect = (session: Session) => {
    setSelectedSession(session);
    setStep(2);
  };
  
  // Handle date selection
  const handleDateSelect = (date: Date) => {
    if (!isDateSelectable(date)) return;
    
    const formattedDate = format(date, 'yyyy-MM-dd');
    setSelectedDate(formattedDate);
    setStep(3);
  };
  
  // Handle time selection
  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setStep(4);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedSession || !selectedDate || !selectedTime || !trainer) return;
    
    try {
      await handleCreateActivity({
        opponent: {
          id: trainer.id,
          name: trainer.name,
          image: trainer.image
        },
        sport: selectedSession.title,
        date: selectedDate,
        time: selectedTime,
        venue: {
          name: 'To be determined',
          address: 'To be determined',
          city: trainer.city
        },
        status: 'new_request',
        direction: 'sent',
        notes: notes
      });
      
      // Success state is handled by the hook
    } catch (error) {
      console.error('Error booking session:', error);
    }
  };
  
  // Check if current step is valid
  const isStepValid = () => {
    switch (step) {
      case 1: return !!selectedSession;
      case 2: return !!selectedDate;
      case 3: return !!selectedTime;
      case 4: return true; // Notes are optional
      default: return false;
    }
  };
  
  // Go to next step
  const handleNext = () => {
    if (step < 4 && isStepValid()) {
      setStep(step + 1);
    } else if (isStepValid()) {
      handleSubmit();
    }
  };
  
  // Go to previous step
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1: // Session Selection
        return (
          <div className="space-y-6">
            <h3 className={cn(
              "text-xl font-semibold",
              isDarkMode ? "text-white" : "text-gray-900"
            )}>
              Select a Session Type
            </h3>
            <div className="space-y-4">
              {sessions.map((session, index) => (
                <div
                  key={index}
                  onClick={() => handleSessionSelect(session)}
                  className={cn(
                    "p-4 rounded-xl cursor-pointer transition-all duration-200",
                    "border hover:shadow-md transform hover:scale-[1.02]",
                    selectedSession === session
                      ? "border-gold-400 bg-gold-400/10"
                      : "border-dark-400 bg-dark-300 hover:bg-dark-400"
                  )}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "p-2 rounded-lg",
                        selectedSession === session
                          ? "bg-gold-400/20"
                          : "bg-dark-400"
                      )}>
                        {session.group_min && session.group_max
                          ? <Users className={cn(
                              "h-5 w-5",
                              selectedSession === session
                                ? "text-gold-400"
                                : "text-dark-800"
                            )} />
                          : <Dumbbell className={cn(
                              "h-5 w-5",
                              selectedSession === session
                                ? "text-gold-400"
                                : "text-dark-800"
                            )} />
                        }
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{session.title}</h4>
                        {session.description && (
                          <p className="text-dark-800 text-sm mt-1">{session.description}</p>
                        )}
                        <div className="flex items-center text-dark-800 text-sm mt-2">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>{session.duration_min} minutes</span>
                        </div>
                        {session.group_min && session.group_max && (
                          <div className="flex items-center text-dark-800 text-sm mt-1">
                            <Users className="h-4 w-4 mr-1" />
                            <span>{session.group_min}-{session.group_max} people</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={cn(
                        "font-bold text-lg",
                        selectedSession === session
                          ? "text-gold-400"
                          : "text-white"
                      )}>
                        {formatPrice(session.price_cents, session.price_mode)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 2: // Date Selection
        return (
          <div className="space-y-6">
            <h3 className={cn(
              "text-xl font-semibold",
              isDarkMode ? "text-white" : "text-gray-900"
            )}>
              Select a Date
            </h3>
            
            <div className="bg-dark-300 rounded-xl p-4">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={handlePreviousMonth}
                  className="p-2 rounded-lg text-dark-800 hover:text-white hover:bg-dark-400 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                <h4 className="text-white font-medium">
                  {format(currentMonth, 'MMMM yyyy')}
                </h4>
                
                <button
                  onClick={handleNextMonth}
                  className="p-2 rounded-lg text-dark-800 hover:text-white hover:bg-dark-400 transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              
              {/* Weekday Headers */}
              <div className="grid grid-cols-7 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                  <div
                    key={index}
                    className="text-center text-dark-800 text-sm font-medium"
                  >
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth(currentMonth).map((date, index) => (
                  <div key={index} className="aspect-square p-1">
                    {date && (
                      <button
                        onClick={() => handleDateSelect(date)}
                        disabled={!isDateSelectable(date)}
                        className={cn(
                          "w-full h-full flex items-center justify-center rounded-lg transition-colors",
                          isSelectedDate(date)
                            ? "bg-gold-400 text-dark-50"
                            : isPastDate(date)
                              ? "text-dark-800 cursor-not-allowed"
                              : "text-white hover:bg-dark-400"
                        )}
                      >
                        {date.getDate()}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Selected Date Display */}
              {selectedDate && (
                <div className="mt-4 pt-4 border-t border-dark-400 text-center">
                  <div className="flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-gold-400 mr-2" />
                    <span className="text-white">
                      {formatDateForDisplay(selectedDate)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      
      case 3: // Time Selection
        return (
          <div className="space-y-6">
            <h3 className={cn(
              "text-xl font-semibold",
              isDarkMode ? "text-white" : "text-gray-900"
            )}>
              Select a Time
            </h3>
            
            <div className="bg-dark-300 rounded-xl p-4">
              <div className="grid grid-cols-3 gap-3">
                {timeSlots.map((time) => (
                  <button
                    key={time}
                    onClick={() => handleTimeSelect(time)}
                    className={cn(
                      "p-3 rounded-lg transition-colors",
                      selectedTime === time
                        ? "bg-gold-400 text-dark-50"
                        : "bg-dark-400 text-white hover:bg-dark-500"
                    )}
                  >
                    {time}
                  </button>
                ))}
              </div>
              
              {/* Selected Time Display */}
              {selectedTime && (
                <div className="mt-4 pt-4 border-t border-dark-400 text-center">
                  <div className="flex items-center justify-center">
                    <Clock className="h-4 w-4 text-gold-400 mr-2" />
                    <span className="text-white">
                      {selectedTime}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      
      case 4: // Confirmation
        return (
          <div className="space-y-6">
            <h3 className={cn(
              "text-xl font-semibold",
              isDarkMode ? "text-white" : "text-gray-900"
            )}>
              Confirm Your Booking
            </h3>
            
            <div className="bg-dark-300 rounded-xl p-4">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <img
                    src={trainer.image}
                    alt={trainer.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-dark-400"
                  />
                  <div>
                    <h4 className="text-white font-medium">{trainer.name}</h4>
                    <p className="text-dark-800 text-sm">{trainer.city}</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-dark-400">
                  <h5 className="text-white font-medium mb-2">Session Details</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-dark-800">Session Type:</span>
                      <span className="text-white">{selectedSession?.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-800">Date:</span>
                      <span className="text-white">{formatDateForDisplay(selectedDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-800">Time:</span>
                      <span className="text-white">{selectedTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-800">Duration:</span>
                      <span className="text-white">{selectedSession?.duration_min} minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-800">Price:</span>
                      <span className="text-gold-400 font-bold">
                        {selectedSession && formatPrice(selectedSession.price_cents, selectedSession.price_mode)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-dark-400">
                  <h5 className="text-white font-medium mb-2">Additional Notes (Optional)</h5>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any specific requirements or questions for the trainer..."
                    className="w-full h-24 bg-dark-400 border border-dark-500 rounded-lg p-3 text-white placeholder-dark-800 resize-none focus:outline-none focus:ring-2 focus:ring-gold-400"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  // Render success state
  const renderSuccessState = () => (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
        <Check className="h-8 w-8 text-dark-50" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">Booking Request Sent!</h3>
      <p className="text-dark-800 mb-3">
        Your booking request has been sent to {trainer.name}. You'll be notified once they respond.
      </p>
      <p className="text-dark-800 mb-6 bg-dark-300 p-3 rounded-lg border border-dark-400 text-sm">
        <strong>Note:</strong> {trainer.name.split(' ')[0]} will need to confirm your request before the session is finalized.
      </p>
      <Button
        onClick={onClose}
        className="bg-gold-400 hover:bg-gold-500 text-dark-50"
      >
        Done
      </Button>
    </div>
  );
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-dark-200 border-dark-300 text-white">
        <DialogHeader>
          <DialogTitle>
            {isSuccess ? 'Booking Confirmed' : 'Book a Session'}
          </DialogTitle>
        </DialogHeader>
        
        {isSuccess ? (
          renderSuccessState()
        ) : (
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="w-full h-1 bg-dark-300 rounded-full overflow-hidden">
              <div
                className="h-full bg-gold-400 transition-all duration-300"
                style={{ width: `${(step / 4) * 100}%` }}
              />
            </div>
            
            {/* Step Content */}
            {renderStepContent()}
            
            {/* Navigation */}
            <div className="flex justify-between items-center pt-4">
              {step > 1 ? (
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="border-dark-400"
                  disabled={isSubmitting}
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div></div>
              )}
              
              <div className="flex items-center space-x-2 text-dark-800">
                <span className="font-medium text-gold-400">{step}</span>
                <span>/</span>
                <span>4</span>
              </div>
              
              <Button
                onClick={handleNext}
                disabled={!isStepValid() || isSubmitting}
                className="bg-gold-400 hover:bg-gold-500 text-dark-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : step === 4 ? (
                  'Confirm Booking'
                ) : (
                  <>
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TrainerBookingModal;